import { StyleSheet } from 'react-native';
import { Tabs, Redirect, usePathname } from 'expo-router';
import { observer } from 'mobx-react-lite';
import { Clapperboard, Home, Tag, Trophy, Users, User } from 'lucide-react-native';

import { AuthGate } from '@/components/AuthGate';
import { authStore } from '@/stores/auth.store';
import { colors, fonts, fontSizes } from '@/constants/theme';

const TabsNavigator = observer(() => {
  const pathname = usePathname();

  if (authStore.needsOnboarding) {
    // Carry the deep link through onboarding too — name.tsx replaces to
    // it after profile completion, so brand-new users still land where
    // they were headed.
    return <Redirect href={{ pathname: '/(onboarding)/name', params: { redirect: pathname } }} />;
  }

  // Users the server lets into MarketPryce Social (athletes, per
  // SOCIAL_ENABLED) get Create where everyone else has Leaderboard. The other
  // one's route still exists, so a link or push to it lands on the tab this
  // user does have.
  const social = authStore.user?.social_enabled === true;
  if (social && pathname === '/leaderboard') return <Redirect href="/create" />;
  if (!social && pathname === '/create') return <Redirect href="/leaderboard" />;

  return (
    <Tabs
      screenOptions={{
        headerShown: false,
        tabBarActiveTintColor: colors.primary,
        tabBarInactiveTintColor: colors.textMuted,
        tabBarShowLabel: true,
        // Pin below-icon. The default picks beside-icon on wide viewports,
        // which reflows the bar in a desktop browser (web is co-equal here).
        tabBarLabelPosition: 'below-icon',
        tabBarLabelStyle: styles.tabLabel,
        // No explicit height: React Navigation only adds the bottom safe-area
        // inset when height is unset, so hardcoding it made the bar sit over
        // the home indicator. Let it compute 49pt + inset.
        tabBarStyle: styles.tabBar,
      }}
    >
      <Tabs.Screen
        name="index"
        options={{ title: 'Home', tabBarIcon: ({ color, size }) => <Home color={color} size={size} /> }}
      />
      <Tabs.Screen
        name="deals"
        options={{ title: 'Deals', tabBarIcon: ({ color, size }) => <Tag color={color} size={size} /> }}
      />
      <Tabs.Screen
        name="create"
        options={{
          title: 'Create',
          href: social ? undefined : null,
          // As tall as its neighbours: the camera glyph sat low and made the label look raised.
          tabBarIcon: ({ color, size }) => <Clapperboard color={color} size={size} />,
        }}
      />
      <Tabs.Screen
        name="leaderboard"
        options={{
          title: 'Leaderboard',
          href: social ? null : undefined,
          tabBarIcon: ({ color, size }) => <Trophy color={color} size={size} />,
        }}
      />
      <Tabs.Screen
        name="referrals"
        options={{
          title: 'Refer',
          tabBarIcon: ({ color, size }) => <Users color={color} size={size} />,
        }}
      />
      <Tabs.Screen
        name="profile"
        options={{ title: 'Me', tabBarIcon: ({ color, size }) => <User color={color} size={size} /> }}
      />
    </Tabs>
  );
});

const styles = StyleSheet.create({
  tabBar: {
    backgroundColor: colors.surface,
    borderTopColor: colors.border,
  },
  tabLabel: {
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.xxs,
  },
});

const TabsLayout = () => (
  <AuthGate>
    <TabsNavigator />
  </AuthGate>
);

export default TabsLayout;
